import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown,
  Building,
  Factory,
  Briefcase,
  Users,
  DollarSign,
  Plus,
  Settings,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  Database,
  Server,
  Shield,
  FileText,
  Calendar,
  Mail,
  Phone,
  X
} from 'lucide-react';
import { useEmpresa } from '../contexts/EmpresaContext';
import api from '../api/config';
import '../styles/SuperAdminPanel.css';

const SuperAdminPanel = () => {
  const [activeTab, setActiveTab] = useState('empresas');
  const [modalCrearEmpresa, setModalCrearEmpresa] = useState(false);
  const [modalEditarEmpresa, setModalEditarEmpresa] = useState(false);
  const [modalCrearSuperAdmin, setModalCrearSuperAdmin] = useState(false);
  const [modalEditarSuperAdmin, setModalEditarSuperAdmin] = useState(false);
  const [empresaEditando, setEmpresaEditando] = useState(null);
  const [superAdminEditando, setSuperAdminEditando] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [empresaFormData, setEmpresaFormData] = useState({});
  const [modalAdministradores, setModalAdministradores] = useState(false);
  const [modalEditarAdmin, setModalEditarAdmin] = useState(false);
  const [empresaAdministradores, setEmpresaAdministradores] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [showDeleteCompanyConfirm, setShowDeleteCompanyConfirm] = useState(false);
  const [deleteCompanySuccess, setDeleteCompanySuccess] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newUserForm, setNewUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    password: '',
    confirmPassword: ''
  });
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotingUser, setPromotingUser] = useState(null);
  const [positions, setPositions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const { empresaActiva, cambiarEmpresa } = useEmpresa();
  const navigate = useNavigate();

  // Ocultar header y bloquear scroll cuando modal está activo en móvil
  useEffect(() => {
    const anyModalOpen = modalCrearEmpresa || modalEditarEmpresa || modalCrearSuperAdmin ||
                         modalEditarSuperAdmin || modalAdministradores || modalEditarAdmin ||
                         showDeleteCompanyConfirm;

    const isMobile = window.innerWidth <= 768;
    const header = document.querySelector('.layout-header');

    // FIX para 100vh en móviles - establecer altura real del viewport
    const setRealViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // Establecer altura directa en modal-container
      const modalContainers = document.querySelectorAll('.modal-container');
      modalContainers.forEach(modal => {
        modal.style.height = `${window.innerHeight}px`;
        modal.style.maxHeight = `${window.innerHeight}px`;
      });
    };

    if (isMobile) {
      if (anyModalOpen) {
        // Ocultar header
        if (header) {
          header.style.display = 'none';
        }
        // Bloquear scroll del body
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        setRealViewportHeight();
        window.addEventListener('resize', setRealViewportHeight);
      } else {
        // Mostrar header
        if (header) {
          header.style.display = '';
        }
        // Restaurar scroll del body
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        window.removeEventListener('resize', setRealViewportHeight);
      }
    }

    return () => {
      if (header) {
        header.style.display = '';
      }
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      window.removeEventListener('resize', setRealViewportHeight);
    };
  }, [modalCrearEmpresa, modalEditarEmpresa, modalCrearSuperAdmin, modalEditarSuperAdmin,
      modalAdministradores, modalEditarAdmin, showDeleteCompanyConfirm]);

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
      // Actualizar la propiedad CSS personalizada
      document.documentElement.style.setProperty('--theme-primary-color', color);
    };

    // Actualizar inicialmente
    updateThemeColor();

    // Actualizar cada 200ms
    const interval = setInterval(updateThemeColor, 200);

    // Escuchar evento personalizado
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  // Cargar datos reales de la API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading SuperAdmin data...');

        const [companiesResponse, usersResponse] = await Promise.all([
          api.get('/superadmin/companies'),
          api.get('/superadmin/users')
        ]);

        console.log('✅ Companies loaded:', companiesResponse.data);
        console.log('✅ Users loaded:', usersResponse.data);

        setCompanies(companiesResponse.data);
        setUsers(usersResponse.data);
      } catch (error) {
        console.error('❌ Error loading SuperAdmin data:', error);
        console.error('Error details:', error.response?.data || error.message);
      } finally {
        setLoading(false);
        console.log('🏁 Loading finished');
      }
    };

    loadData();
  }, []);

  const [nuevaEmpresa, setNuevaEmpresa] = useState({
    rut: '',
    razonSocial: '',
    nombreFantasia: '',
    giro: '',
    direccion: '',
    comuna: '',
    ciudad: '',
    telefono: '',
    email: '',
    contactoNombre: '',
    contactoEmail: '',
    adminNombre: '',
    adminEmail: '',
    adminPassword: 'Admin123!'
  });

  const [adminEmailExists, setAdminEmailExists] = useState(null); // null = no verificado, true = existe, false = no existe
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState(null);

  // Verificar si el email del admin ya existe
  const verificarEmailAdmin = async (email) => {
    if (!email || !email.includes('@')) {
      setAdminEmailExists(null);
      setExistingUserInfo(null);
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await api.get(`/superadmin/debug/user/${email}`);
      if (response.data.exists) {
        setAdminEmailExists(true);
        setExistingUserInfo({
          nombre: `${response.data.user.firstName} ${response.data.user.lastName}`,
          empresas: response.data.companies.length
        });
      } else {
        setAdminEmailExists(false);
        setExistingUserInfo(null);
      }
    } catch (error) {
      console.error('Error verificando email:', error);
      setAdminEmailExists(null);
      setExistingUserInfo(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Función para cerrar el modal de crear empresa y resetear estados
  const cerrarModalCrearEmpresa = () => {
    setModalCrearEmpresa(false);
    setActionSuccess(null);
    setAdminEmailExists(null);
    setExistingUserInfo(null);
    setCheckingEmail(false);
    setNuevaEmpresa({
      rut: '',
      razonSocial: '',
      nombreFantasia: '',
      giro: '',
      direccion: '',
      comuna: '',
      ciudad: '',
      telefono: '',
      email: '',
      contactoNombre: '',
      contactoEmail: '',
      adminNombre: '',
      adminEmail: '',
      adminPassword: 'Admin123!'
    });
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      activa: { label: 'Activa', color: themeColor, icon: CheckCircle },
      suspendida: { label: 'Suspendida', color: '#ffb3ba', icon: AlertCircle }
    };

    const estadoInfo = estados[estado] || { label: estado, color: '#6b7280', icon: AlertCircle };
    const IconComponent = estadoInfo.icon;

    return (
      <span className="estado-badge" style={{ backgroundColor: estadoInfo.color }}>
        <IconComponent size={12} />
        {estadoInfo.label}
      </span>
    );
  };


  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const formatRut = (value) => {
    // Limpiar el valor de puntos y guiones
    let cleanValue = value.replace(/[^\dkK]/g, '').toUpperCase();

    // Limitar a 9 caracteres máximo (8 dígitos + 1 DV)
    if (cleanValue.length > 9) {
      cleanValue = cleanValue.slice(0, 9);
    }

    if (!cleanValue) return '';

    // Separar cuerpo y dígito verificador
    const body = cleanValue.slice(0, -1);
    const dv = cleanValue.slice(-1);

    if (cleanValue.length <= 1) return cleanValue;

    // Formatear el cuerpo con puntos
    const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${formattedBody}-${dv}`;
  };

  const handleRutChange = (value, field = 'rut') => {
    const formatted = formatRut(value);
    if (field === 'rut') {
      setNuevaEmpresa(prev => ({ ...prev, rut: formatted }));
    } else if (field === 'empresaRut') {
      setEmpresaFormData(prev => ({ ...prev, taxId: formatted }));
    }
  };

  const handleCrearEmpresa = async () => {
    try {
      console.log('Crear nueva empresa:', nuevaEmpresa);

      // Validar campos obligatorios
      if (!nuevaEmpresa.nombreFantasia) {
        setActionSuccess({ type: 'error', message: 'Por favor completa el Nombre Fantasía' });
        return;
      }

      if (!nuevaEmpresa.adminNombre || !nuevaEmpresa.adminEmail || !nuevaEmpresa.adminPassword) {
        setActionSuccess({ type: 'error', message: 'Por favor completa los datos del administrador principal' });
        return;
      }

      // Crear la empresa
      const companyData = {
        taxId: nuevaEmpresa.rut || null,
        legalName: nuevaEmpresa.razonSocial || nuevaEmpresa.nombreFantasia,
        tradeName: nuevaEmpresa.nombreFantasia,
        businessActivity: nuevaEmpresa.giro || null,
        email: nuevaEmpresa.email || null,
        phone: nuevaEmpresa.telefono || null,
        isActive: true
      };

      const companyResponse = await api.post('/superadmin/companies', companyData);
      const companyId = companyResponse.data.id;

      console.log('✅ Empresa creada:', companyResponse.data);

      // Verificar si el usuario ya existe
      let userId = null;
      let isNewUser = false;

      try {
        const checkUserResponse = await api.get(`/superadmin/debug/user/${nuevaEmpresa.adminEmail}`);

        if (checkUserResponse.data.exists) {
          // Usuario ya existe, usarlo
          userId = checkUserResponse.data.user.id;
          console.log('✅ Usuario existente encontrado:', userId);

          // Promover el usuario existente a admin de esta empresa con contraseña
          await api.post(`/superadmin/users/${userId}/promote`, {
            companyId: companyId,
            role: 'admin',
            position: 'Administrador Principal',
            password: nuevaEmpresa.adminPassword // Contraseña específica para esta empresa
          });
          console.log('✅ Usuario existente promovido a admin de la nueva empresa');
        } else {
          // Usuario no existe, crearlo
          isNewUser = true;
          const adminData = {
            firstName: nuevaEmpresa.adminNombre.split(' ')[0],
            lastName: nuevaEmpresa.adminNombre.split(' ').slice(1).join(' ') || nuevaEmpresa.adminNombre.split(' ')[0],
            email: nuevaEmpresa.adminEmail,
            password: nuevaEmpresa.adminPassword,
            phone: nuevaEmpresa.telefono || '',
            position: 'Administrador Principal',
            companyId: companyId,
            role: 'admin'
          };

          const adminResponse = await api.post('/superadmin/create-admin', adminData);
          console.log('✅ Nuevo administrador creado:', adminResponse.data);
        }
      } catch (error) {
        console.error('Error al verificar/crear usuario:', error);
        throw error;
      }

      // Mostrar mensaje de éxito con las credenciales
      let successMessage = `Empresa creada exitosamente\n\nAdministrador Principal:\nEmail: ${nuevaEmpresa.adminEmail}\nContraseña para ${nuevaEmpresa.nombreFantasia}: ${nuevaEmpresa.adminPassword}\n`;

      if (isNewUser) {
        successMessage += `\nSe creó un nuevo usuario.\nPuede iniciar sesión con estas credenciales.`;
      } else {
        successMessage += `\nSe asignó el usuario existente con una contraseña específica para esta empresa.\nSi tiene otras empresas, puede tener contraseñas diferentes.`;
      }

      setActionSuccess({
        type: 'success',
        message: successMessage
      });

      // Recargar la lista de empresas
      const companiesResponse = await api.get('/superadmin/companies');
      setCompanies(companiesResponse.data);

    } catch (error) {
      console.error('❌ Error al crear empresa:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Error al crear la empresa. Por favor intenta de nuevo.';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Datos inválidos. Por favor verifica el Nombre Fantasía y los datos del administrador.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Error del servidor al crear la empresa. Verifica que todos los datos sean correctos.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      setActionSuccess({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const accederAEmpresa = (empresa) => {
    console.log('🔄 Accediendo a empresa:', empresa);

    // Mapear la empresa del backend al formato que espera el contexto
    const empresaFormateada = {
      id: empresa.id,
      nombreFantasia: empresa.tradeName,
      razonSocial: empresa.legalName,
      rut: empresa.taxId,
      rolUsuario: 'Super Admin',
      permisos: {
        crear_cotizaciones: true,
        aprobar_cotizaciones: true,
        ver_margenes: true,
        ver_costos: true,
        gestionar_usuarios: true,
        configurar_empresa: true,
        todos: true
      }
    };

    console.log('✅ Empresa formateada para contexto:', empresaFormateada);
    cambiarEmpresa(empresaFormateada);

    console.log('🔄 Navegando al dashboard...');
    // Navegar al dashboard de la empresa
    navigate('/dashboard');

    // Scroll al inicio de la página
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const abrirModalEditarEmpresa = (empresa) => {
    setEmpresaEditando(empresa);
    setEmpresaFormData({
      taxId: empresa.taxId,
      legalName: empresa.legalName,
      tradeName: empresa.tradeName,
      email: empresa.email,
      phone: empresa.phone || '',
      isActive: empresa.isActive
    });
    setModalEditarEmpresa(true);
  };

  const cerrarModalEditarEmpresa = () => {
    setEmpresaEditando(null);
    setModalEditarEmpresa(false);
  };

  const abrirModalEditarSuperAdmin = (admin) => {
    setSuperAdminEditando(admin);
    setEditFormData({
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      phone: admin.phone || '',
      password: '',
      isActive: admin.isActive
    });
    setModalEditarSuperAdmin(true);
  };

  const cerrarModalEditarSuperAdmin = () => {
    setSuperAdminEditando(null);
    setModalEditarSuperAdmin(false);
  };

  const actualizarSuperAdmin = async (adminData) => {
    try {
      const response = await api.put(`/superadmin/users/${superAdminEditando.id}`, adminData);
      console.log('✅ SuperAdmin actualizado:', response.data);

      // Recargar datos
      const usersResponse = await api.get('/superadmin/users');
      setUsers(usersResponse.data);

      alert('✅ Super Admin actualizado exitosamente');
      cerrarModalEditarSuperAdmin();
    } catch (error) {
      console.error('❌ Error actualizando SuperAdmin:', error);
      alert('❌ Error al actualizar el Super Admin');
    }
  };

  const actualizarEmpresa = async (empresaData) => {
    try {
      // Validar campo obligatorio
      if (!empresaData.tradeName) {
        alert('Por favor completa el Nombre Fantasía');
        return;
      }

      // Si no hay legalName, usar tradeName
      const dataToSend = {
        ...empresaData,
        legalName: empresaData.legalName || empresaData.tradeName
      };

      const response = await api.put(`/superadmin/companies/${empresaEditando.id}`, dataToSend);
      console.log('✅ Empresa actualizada:', response.data);

      // Recargar datos
      const companiesResponse = await api.get('/superadmin/companies');
      setCompanies(companiesResponse.data);

      alert('✅ Empresa actualizada exitosamente');
      cerrarModalEditarEmpresa();
    } catch (error) {
      console.error('❌ Error actualizando empresa:', error);
      alert('❌ Error al actualizar la empresa');
    }
  };

  const abrirModalAdministradores = async (empresa) => {
    console.log('🔍 Abriendo modal de administradores para:', empresa);

    try {
      // Obtener los administradores reales de la base de datos
      const response = await api.get(`/superadmin/companies/${empresa.id}/users`);
      console.log('📊 Datos de usuarios de la empresa:', response.data);

      // Filtrar solo los usuarios con rol de admin
      const administradores = response.data
        .filter(userCompany => userCompany.role === 'admin' && userCompany.isActive)
        .map(userCompany => ({
          id: userCompany.User.id,
          firstName: userCompany.User.firstName,
          lastName: userCompany.User.lastName,
          email: userCompany.User.email,
          role: userCompany.role, // Usar el rol real de la base de datos
          phone: userCompany.User.phone || '+56999999999',
          lastLogin: userCompany.User.lastLogin || 'Nunca'
        }));

      // Si no hay administradores, mostrar mensaje
      if (administradores.length === 0) {
        administradores.push({
          id: 0,
          firstName: 'Sin',
          lastName: 'Administradores',
          email: 'No hay administradores activos',
          role: 'N/A',
          phone: 'N/A',
          lastLogin: 'N/A'
        });
      }

      console.log('✅ Administradores desde BD:', administradores);
      setEmpresaAdministradores({ empresa, administradores });
      setModalAdministradores(true);
    } catch (error) {
      console.error('❌ Error obteniendo administradores:', error);

      // Fallback a datos básicos si falla la API
      const administradores = [{
        id: 0,
        firstName: 'Error',
        lastName: 'Cargando',
        email: 'Error al cargar administradores',
        role: 'N/A',
        phone: 'N/A',
        lastLogin: 'N/A'
      }];

      setEmpresaAdministradores({ empresa, administradores });
      setModalAdministradores(true);
    }
  };

  const getAdminCount = (empresa) => {
    // Buscar en los usuarios cuántos tienen rol de admin en esta empresa
    const companyUsers = empresa.companyUsers || [];
    const adminCount = companyUsers.filter(user => user.role === 'admin' && user.isActive).length;
    return adminCount;
  };

  const openAddAdmin = async () => {
    try {
      // Cargar miembros del equipo actual de la empresa
      const response = await api.get(`/superadmin/companies/${empresaAdministradores.empresa.id}/users`);

      // Filtrar usuarios que NO son administradores (pueden ser promovidos)
      const nonAdmins = response.data
        .filter(userCompany => userCompany.role !== 'admin' && userCompany.isActive)
        .map(userCompany => ({
          id: userCompany.User.id,
          firstName: userCompany.User.firstName,
          lastName: userCompany.User.lastName,
          email: userCompany.User.email,
          role: userCompany.role,
          position: userCompany.position || '',
          phone: userCompany.User.phone || ''
        }));

      setTeamMembers(nonAdmins);
      setShowAddAdmin(true);
    } catch (error) {
      console.error('Error cargando equipo:', error);
      setTeamMembers([]);
      setShowAddAdmin(true);
    }
  };

  const promoteToAdmin = async (userId) => {
    try {
      setPromotingUser(userId);

      // Promover usuario existente a administrador
      await api.patch(`/superadmin/users/${userId}`, {
        role: 'admin',
        companyId: empresaAdministradores.empresa.id
      });

      setActionSuccess({
        type: 'roleChange',
        message: 'Usuario promovido a administrador exitosamente',
        user: teamMembers.find(m => m.id === userId)
      });

      // Recargar datos después de 2 segundos
      setTimeout(async () => {
        setPromotingUser(null);
        setShowAddAdmin(false);
        setActionSuccess(null);
        await abrirModalAdministradores(empresaAdministradores.empresa);
      }, 2000);
    } catch (error) {
      console.error('Error promoviendo usuario:', error);
      setPromotingUser(null);
      setActionSuccess({
        type: 'error',
        message: 'Error al promover usuario a administrador',
        user: teamMembers.find(m => m.id === userId)
      });

      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    }
  };

  // Función para validar requisitos de seguridad de contraseña
  const validatePassword = (pass) => {
    const hasMinLength = pass.length >= 8;
    const hasMaxLength = pass.length <= 20;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass);

    return {
      isValid: hasMinLength && hasMaxLength && hasUpperCase && hasLowerCase && hasNumber && hasSymbol,
      hasMinLength,
      hasMaxLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSymbol
    };
  };

  // Función para verificar si el formulario es válido
  const isFormValid = useMemo(() => {
    try {
      const hasRequiredFields = newUserForm.firstName && newUserForm.email && newUserForm.password && newUserForm.confirmPassword;
      const passwordsMatch = newUserForm.password === newUserForm.confirmPassword;
      const passwordValid = newUserForm.password ? validatePassword(newUserForm.password).isValid : false;

      return hasRequiredFields && passwordsMatch && passwordValid;
    } catch (error) {
      console.error('Error en isFormValid:', error);
      return false;
    }
  }, [newUserForm.firstName, newUserForm.email, newUserForm.password, newUserForm.confirmPassword]);

  const loadPositions = async (companyId) => {
    try {
      // Ahora siempre pasamos el companyId para obtener solo los cargos de esa empresa
      const response = await api.get('/catalog/positions', {
        params: { companyId }
      });
      const positionsData = response.data;

      // Asegurar que siempre sea un array
      if (Array.isArray(positionsData)) {
        setPositions(positionsData);
      } else if (positionsData && positionsData.data) {
        setPositions(positionsData.data || []);
      } else {
        console.warn('API no devolvió un array para positions');
        setPositions([]);
      }
    } catch (error) {
      console.error('Error cargando posiciones:', error);
      setPositions([]);
    }
  };

  const promoteExistingUser = async (email) => {
    try {
      setCreatingAdmin(true);

      // Buscar al usuario por email
      const usersResponse = await api.get('/superadmin/users');
      const existingUser = usersResponse.data.find(user => user.email === email);

      if (!existingUser) {
        throw new Error('Usuario no encontrado');
      }

      // Promover al usuario a administrador de esta empresa
      await api.post(`/superadmin/users/${existingUser.id}/promote`, {
        companyId: empresaAdministradores.empresa.id,
        role: 'admin',
        position: newUserForm.position || 'Administrador'
      });

      setActionSuccess({
        type: 'roleChange',
        message: `${existingUser.firstName} ${existingUser.lastName} ha sido promovido a administrador`,
        user: existingUser
      });

      // Limpiar formulario
      setNewUserForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        password: '',
        confirmPassword: ''
      });
      setShowChangePassword(false);

      // Recargar administradores después de 2 segundos
      setTimeout(async () => {
        setActionSuccess(null);
        setShowCreateAdmin(false);
        await abrirModalAdministradores(empresaAdministradores.empresa);
      }, 2000);

    } catch (error) {
      console.error('Error promoviendo usuario:', error);
      setCreatingAdmin(false);
      setActionSuccess({
        type: 'error',
        message: 'Error al promover usuario a administrador',
        user: newUserForm
      });

      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    }
  };

  const createNewAdmin = async () => {
    try {
      setCreatingAdmin(true);

      // Validar formulario - solo nombre, email y contraseña son obligatorios
      if (!newUserForm.firstName || !newUserForm.email || !newUserForm.password) {
        alert('Por favor completa los campos obligatorios: Nombre, Email y Contraseña');
        setCreatingAdmin(false);
        return;
      }

      // Validar contraseña
      const passwordValidation = validatePassword(newUserForm.password);
      if (!passwordValidation.isValid) {
        alert('La contraseña no cumple con los requisitos de seguridad');
        setCreatingAdmin(false);
        return;
      }

      // Validar confirmación de contraseña
      if (newUserForm.password !== newUserForm.confirmPassword) {
        alert('Las contraseñas no coinciden');
        setCreatingAdmin(false);
        return;
      }

      // Crear nuevo usuario directamente como administrador
      const userData = {
        firstName: newUserForm.firstName,
        lastName: newUserForm.lastName,
        email: newUserForm.email,
        phone: newUserForm.phone || null,
        position: newUserForm.position || 'Administrador',
        password: newUserForm.password, // Usar la contraseña del formulario
        role: 'admin',
        companyId: empresaAdministradores.empresa.id
      };

      console.log('📤 Datos que se envían al servidor:', userData);

      const response = await api.post('/superadmin/create-admin', userData);

      setActionSuccess({
        type: 'roleChange',
        message: `Nuevo administrador ${newUserForm.firstName} ${newUserForm.lastName} creado exitosamente`,
        user: newUserForm
      });

      // Limpiar formulario
      setNewUserForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        department: '',
        password: '',
        confirmPassword: ''
      });

      // Recargar datos después de 2 segundos y volver a la lista principal
      setTimeout(async () => {
        setActionSuccess(null);
        setShowCreateNew(false);
        setShowAddAdmin(false);
        setCreatingAdmin(false);

        // Recargar la lista de administradores sin cambiar la vista
        const response = await api.get(`/superadmin/companies/${empresaAdministradores.empresa.id}/users`);
        const administradores = response.data
          .filter(userCompany => userCompany.role === 'admin' && userCompany.isActive)
          .map(userCompany => ({
            id: userCompany.User.id,
            firstName: userCompany.User.firstName,
            lastName: userCompany.User.lastName,
            email: userCompany.User.email,
            role: userCompany.role,
            phone: userCompany.User.phone || '+56999999999',
            lastLogin: userCompany.User.lastLogin || 'Nunca'
          }));

        setEmpresaAdministradores({
          empresa: empresaAdministradores.empresa,
          administradores: administradores
        });
      }, 2000);
    } catch (error) {
      console.error('❌ Error creando nuevo admin:', error);
      console.error('📊 Response status:', error.response?.status);
      console.error('📋 Response data:', error.response?.data);
      console.error('🔍 Response headers:', error.response?.headers);
      console.error('⚙️ Request config:', error.config);
      setCreatingAdmin(false);

      // Mostrar error específico del servidor si está disponible
      const errorMessage = error.response?.data?.message || error.message || 'Error al crear nuevo administrador';

      // Si el usuario ya existe, sugerir promoverlo
      if (errorMessage.includes('already exists')) {
        setActionSuccess({
          type: 'error',
          message: `El email ${newUserForm.email} ya está registrado. ¿Quieres promover a este usuario a administrador?`,
          showPromoteOption: true,
          existingUser: newUserForm
        });
      } else {
        setActionSuccess({
          type: 'error',
          message: errorMessage,
          user: newUserForm
        });
      }

      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Administrador',
      'salesperson': 'Editor',
      'editor': 'Editor',
      'viewer': 'Miembro',
      'member': 'Miembro',
      'accountant': 'Miembro',
      'buyer': 'Miembro',
      'seller': 'Editor',
      'supervisor': 'Editor',
      'manager': 'Administrador',
      'employee': 'Miembro'
    };
    return roleMap[role] || 'Miembro';
  };

  const cerrarModalAdministradores = async () => {
    setEmpresaAdministradores(null);
    setModalAdministradores(false);
    setShowDeleteConfirm(false);
    setAdminToDelete(null);
    setActionSuccess(null);
    setShowAddAdmin(false);

    // Recargar datos al cerrar el modal
    try {
      const [companiesResponse, usersResponse] = await Promise.all([
        api.get('/superadmin/companies'),
        api.get('/superadmin/users')
      ]);

      console.log('✅ Datos actualizados después de cerrar modal');
      setCompanies(companiesResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error('Error recargando datos:', error);
    }
  };

  const handleEditAdmin = async (admin) => {
    // Cargar posiciones si no están cargadas
    if (positions.length === 0) {
      await loadPositions();
    }

    // Preparar datos para edición
    setEditFormData({
      id: admin.id,
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      phone: admin.phone,
      role: admin.role,
      position: admin.position || '',
      isActive: admin.isActive !== false
    });
    setModalEditarAdmin(true);
  };

  const handleDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteConfirm(true);
    setActionSuccess(null);
  };

  const cancelDeleteAdmin = () => {
    setAdminToDelete(null);
    setShowDeleteConfirm(false);
    setActionSuccess(null);
  };

  const changeAdminRole = async (newRole) => {
    console.log('Cambiando rol de', adminToDelete.firstName, adminToDelete.lastName, 'a', newRole);

    try {
      // Mapeo de roles - usar roles válidos del ENUM
      const roleMapping = {
        'Editor': 'editor',
        'Miembro': 'member',
        'Administrador': 'admin'
      };

      // Intentar actualizar en el backend
      try {
        // Primero intentar con PATCH
        await api.patch(`/superadmin/users/${adminToDelete.id}`, {
          role: roleMapping[newRole] || newRole.toLowerCase(),
          companyId: empresaAdministradores.empresa.id
        });
        console.log('✅ Rol actualizado en el backend con PATCH');
      } catch (patchError) {
        // Si PATCH falla, intentar con PUT
        try {
          await api.put(`/superadmin/users/${adminToDelete.id}/role`, {
            role: roleMapping[newRole] || newRole.toLowerCase(),
            companyId: empresaAdministradores.empresa.id
          });
          console.log('✅ Rol actualizado en el backend con PUT');
        } catch (putError) {
          console.warn('⚠️ No se pudo actualizar en el backend, aplicando cambios localmente:', putError.message);
          // Continuar con la actualización local aunque falle el backend
        }
      }

      // Si cambia a Editor o Miembro, quitar de la lista de administradores localmente
      if (newRole === 'Editor' || newRole === 'Miembro') {
        const filteredAdmins = empresaAdministradores.administradores.filter(
          admin => admin.id !== adminToDelete.id
        );
        setEmpresaAdministradores({ ...empresaAdministradores, administradores: filteredAdmins });
      }

      setActionSuccess({
        type: 'roleChange',
        message: `${adminToDelete.firstName} ${adminToDelete.lastName} ahora es ${newRole}`,
        user: adminToDelete,
        newRole: newRole
      });

      // Volver a la lista después de 2 segundos
      setTimeout(async () => {
        setAdminToDelete(null);
        setShowDeleteConfirm(false);
        setActionSuccess(null);

        // Recargar los datos de empresas y usuarios
        try {
          const [companiesResponse, usersResponse] = await Promise.all([
            api.get('/superadmin/companies'),
            api.get('/superadmin/users')
          ]);

          setCompanies(companiesResponse.data);
          setUsers(usersResponse.data);
        } catch (err) {
          console.log('No se pudieron recargar los datos, usando cache local');
        }
      }, 2000);
    } catch (error) {
      console.error('Error cambiando rol:', error);
      alert('Error al cambiar el rol. Por favor intenta de nuevo.');
      setShowDeleteConfirm(false);
      setAdminToDelete(null);
    }
  };

  const removeAdminFromCompany = async () => {
    console.log('Eliminando', adminToDelete.firstName, adminToDelete.lastName, 'de la empresa');

    try {
      // Intentar eliminar en el backend
      try {
        await api.delete(`/superadmin/companies/${empresaAdministradores.empresa.id}/users/${adminToDelete.id}`);
        console.log('✅ Usuario eliminado del backend');
      } catch (deleteError) {
        // Si falla, intentar con un PATCH para desactivar
        try {
          await api.patch(`/superadmin/users/${adminToDelete.id}`, {
            isActive: false,
            companyId: null
          });
          console.log('✅ Usuario desactivado en el backend con PATCH');
        } catch (patchError) {
          console.warn('⚠️ No se pudo eliminar en el backend, aplicando cambios localmente:', deleteError.message);
          // Continuar con la eliminación local aunque falle el backend
        }
      }

      // Quitar de la lista de administradores localmente
      const filteredAdmins = empresaAdministradores.administradores.filter(
        admin => admin.id !== adminToDelete.id
      );
      setEmpresaAdministradores({ ...empresaAdministradores, administradores: filteredAdmins });

      setActionSuccess({
        type: 'remove',
        message: `${adminToDelete.firstName} ${adminToDelete.lastName} ha sido eliminado de la empresa`,
        user: adminToDelete
      });

      // Volver a la lista después de 2 segundos
      setTimeout(async () => {
        setAdminToDelete(null);
        setShowDeleteConfirm(false);
        setActionSuccess(null);

        // Recargar los datos de empresas y usuarios
        try {
          const [companiesResponse, usersResponse] = await Promise.all([
            api.get('/superadmin/companies'),
            api.get('/superadmin/users')
          ]);

          setCompanies(companiesResponse.data);
          setUsers(usersResponse.data);
        } catch (err) {
          console.log('No se pudieron recargar los datos, usando cache local');
        }
      }, 2000);
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      alert('Error al eliminar el usuario. Por favor intenta de nuevo.');
      setShowDeleteConfirm(false);
      setAdminToDelete(null);
    }
  };

  const eliminarEmpresa = async () => {
    if (!empresaToDelete) return;

    console.log('Eliminando empresa:', empresaToDelete.tradeName);

    try {
      // Llamar al API para eliminar la empresa y todos sus datos
      await api.delete(`/superadmin/companies/${empresaToDelete.id}`);
      console.log('✅ Empresa eliminada del backend');

      // Recargar la lista de empresas y usuarios
      const [companiesResponse, usersResponse] = await Promise.all([
        api.get('/superadmin/companies'),
        api.get('/superadmin/users')
      ]);

      setCompanies(companiesResponse.data);
      setUsers(usersResponse.data);

      // Mostrar mensaje de éxito en el modal
      setDeleteCompanySuccess({ type: 'success', empresaNombre: empresaToDelete.tradeName });
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Error al eliminar la empresa. Por favor intenta de nuevo.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      // Mostrar mensaje de error en el modal
      setDeleteCompanySuccess({ type: 'error', message: errorMessage });
    }
  };

  const estadisticasGlobales = {
    totalEmpresas: companies.length,
    empresasActivas: companies.filter(c => c.isActive).length,
    usuariosTotales: companies.reduce((sum, c) => sum + (c.userCount || 0), 0),
    superAdmins: users.filter(u => u.isSuperAdmin).length
  };

  return (
    <div className="super-admin-container">
      {/* Header del Super Admin */}
      <div className="super-admin-header">
        <div className="header-title">
          <svg width="32" height="32" className="crown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
          <div>
            <h1>Panel Super Administrador</h1>
            <p>
              {empresaActiva
                ? `Administrando: ${empresaActiva.nombreFantasia}`
                : 'Gestión completa del sistema PAPER BOAT'}
            </p>
          </div>
        </div>

        <div className="admin-stats">
          <div className="stat-card">
            <Briefcase size={24} />
            <div>
              <span className="stat-number">{estadisticasGlobales.totalEmpresas}</span>
              <span className="stat-label">Empresas</span>
            </div>
          </div>
          <div className="stat-card">
            <Users size={20} />
            <div>
              <span className="stat-number">{estadisticasGlobales.usuariosTotales}</span>
              <span className="stat-label">Usuarios Activos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navegación de tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'empresas' ? 'active' : ''}`}
          onClick={() => setActiveTab('empresas')}
          style={activeTab === 'empresas' ? {
            backgroundColor: themeColor,
            color: '#000',
            borderColor: '#000'
          } : {}}
        >
          <Building size={18} />
          Empresas
        </button>
        <button
          className={`tab ${activeTab === 'sistema' ? 'active' : ''}`}
          onClick={() => setActiveTab('sistema')}
          style={activeTab === 'sistema' ? {
            backgroundColor: themeColor,
            color: '#000',
            borderColor: '#000'
          } : {}}
        >
          <Server size={18} />
          Sistema
        </button>
        <button
          className={`tab ${activeTab === 'actividad' ? 'active' : ''}`}
          onClick={() => setActiveTab('actividad')}
          style={activeTab === 'actividad' ? {
            backgroundColor: themeColor,
            color: '#000',
            borderColor: '#000'
          } : {}}
        >
          <Activity size={18} />
          Actividad
        </button>
        <button
          className={`tab ${activeTab === 'superadmins' ? 'active' : ''}`}
          onClick={() => setActiveTab('superadmins')}
          style={activeTab === 'superadmins' ? {
            backgroundColor: themeColor,
            color: '#000',
            borderColor: '#000'
          } : {}}
        >
          <Shield size={18} />
          Super Admins
        </button>
      </div>

      {/* Contenido según el tab activo */}
      <div className="admin-content">
        {activeTab === 'empresas' && (
          <div className="empresas-section">
            <div className="section-header">
              <h2>Gestión de Empresas Cliente</h2>
              <button
                className="btn-crear-empresa"
                onClick={() => setModalCrearEmpresa(true)}
                style={{
                  backgroundColor: themeColor,
                  color: '#000',
                  border: '2px solid #000'
                }}
              >
                <Plus size={18} />
                Nueva Empresa
              </button>
            </div>

            <div className="empresas-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Empresa</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Estado</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Usuarios</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Administradores</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                        Cargando empresas...
                      </td>
                    </tr>
                  ) : companies.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                        No hay empresas registradas
                      </td>
                    </tr>
                  ) : companies.map((empresa) => (
                    <tr key={empresa.id}>
                      <td data-label="Empresa">
                        <div className="empresa-info">
                          <div>
                            <strong>{empresa.tradeName}</strong>
                            <br />
                            <small>{empresa.taxId}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Estado">{getEstadoBadge(empresa.isActive ? 'activa' : 'suspendida')}</td>
                      <td data-label="Usuarios">
                        <span>
                          {empresa.userCount || 0} usuarios
                        </span>
                      </td>
                      <td data-label="Administradores">
                        <div className="admin-summary">
                          <span className="admin-count">
                            {getAdminCount(empresa)} administrador{getAdminCount(empresa) !== 1 ? 'es' : ''}
                          </span>
                          <button
                            className="btn-ver-admins"
                            onClick={() => abrirModalAdministradores(empresa)}
                            title="Ver administradores"
                            style={{
                              backgroundColor: themeColor,
                              color: '#000',
                              border: '1px solid #000',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Administradores
                          </button>
                        </div>
                      </td>
                      <td data-label="Acciones">
                        <div className="action-buttons">
                          <button
                            className="btn-action"
                            onClick={() => accederAEmpresa(empresa)}
                            title="Acceder como Super Admin"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn-action"
                            title="Editar"
                            onClick={() => abrirModalEditarEmpresa(empresa)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn-action btn-danger"
                            title="Eliminar Empresa"
                            onClick={() => {
                              setEmpresaToDelete(empresa);
                              setShowDeleteCompanyConfirm(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'sistema' && (
          <div className="sistema-section">
            <h2>Estado del Sistema</h2>
            <div className="sistema-stats">
              <div className="sistema-card">
                <Activity size={24} />
                <h3>Sistema Operativo</h3>
                <p>Todos los servicios funcionando correctamente</p>
                <span className="status-ok">🟢 Online</span>
              </div>
              <div className="sistema-card">
                <Database size={24} />
                <h3>Base de Datos</h3>
                <p>Rendimiento óptimo - 2.3ms promedio</p>
                <span className="status-ok">🟢 Saludable</span>
              </div>
              <div className="sistema-card">
                <Shield size={24} />
                <h3>Seguridad</h3>
                <p>Última actualización: Hace 2 días</p>
                <span className="status-ok">🟢 Protegido</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'actividad' && (
          <div className="actividad-section">
            <h2>Estadísticas del Sistema</h2>
            <div className="actividad-stats">
              <div className="actividad-card">
                <h3>Empresas Activas</h3>
                <span className="amount">{estadisticasGlobales.empresasActivas}</span>
                <small>De {estadisticasGlobales.totalEmpresas} totales</small>
              </div>
              <div className="actividad-card">
                <h3>Usuarios Totales</h3>
                <span className="amount">{estadisticasGlobales.usuariosTotales}</span>
                <small>En todas las empresas</small>
              </div>
              <div className="actividad-card">
                <h3>Super Administradores</h3>
                <span className="amount">{estadisticasGlobales.superAdmins}</span>
                <small>Con acceso completo</small>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'superadmins' && (
          <div className="superadmins-section">
            <div className="section-header">
              <h2>Gestión de Super Administradores</h2>
              <button
                className="btn-crear-empresa"
                onClick={() => setModalCrearSuperAdmin(true)}
                style={{
                  backgroundColor: themeColor,
                  color: '#000',
                  border: '2px solid #000'
                }}
              >
                <Plus size={18} />
                Nuevo Super Admin
              </button>
            </div>

            <div className="empresas-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Nombre</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Email</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Teléfono</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Último Acceso</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Estado</th>
                    <th style={{ color: themeColor, padding: '16px', fontWeight: '600' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                        Cargando super administradores...
                      </td>
                    </tr>
                  ) : users.filter(u => u.isSuperAdmin).length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                        No hay super administradores registrados
                      </td>
                    </tr>
                  ) : users.filter(u => u.isSuperAdmin).map((admin) => (
                    <tr key={admin.id}>
                      <td>
                        <div className="empresa-info">
                          <strong>{admin.firstName} {admin.lastName}</strong>
                          <small>Super Administrador</small>
                        </div>
                      </td>
                      <td>{admin.email}</td>
                      <td>{admin.phone || 'No especificado'}</td>
                      <td>{admin.lastLogin ? new Date(admin.lastLogin).toLocaleString() : 'Nunca'}</td>
                      <td>{getEstadoBadge(admin.isActive ? 'activa' : 'suspendida')}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action"
                            title="Editar Super Admin"
                            onClick={() => abrirModalEditarSuperAdmin(admin)}
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #000' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                <strong>Nota:</strong> Los Super Administradores tienen acceso completo al sistema y pueden gestionar todas las empresas.
                Solo otorga estos permisos a personal de confianza.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear nueva empresa */}
      {modalCrearEmpresa && (
        <div className="modal-overlay">
          <div className="modal-container">
            {!actionSuccess ? (
              <>
                <div className="modal-header" style={{
                  background: themeColor,
                  borderBottom: '3px solid #000'
                }}>
                  <h2 style={{ color: '#000', margin: 0, fontSize: '24px', fontWeight: '700' }}>Crear Nueva Empresa Cliente</h2>
                  <button onClick={() => cerrarModalCrearEmpresa()} style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: '#000',
                    fontWeight: 'bold'
                  }}>×</button>
                </div>
                <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>RUT</label>
                  <input
                    type="text"
                    value={nuevaEmpresa.rut}
                    onChange={(e) => handleRutChange(e.target.value, 'rut')}
                    placeholder="76.123.456-7"
                  />
                </div>
                <div className="form-group">
                  <label>Razón Social</label>
                  <input
                    type="text"
                    value={nuevaEmpresa.razonSocial}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, razonSocial: e.target.value }))}
                    placeholder="EMPRESA EJEMPLO SPA"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre Fantasía *</label>
                  <input
                    type="text"
                    value={nuevaEmpresa.nombreFantasia}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, nombreFantasia: e.target.value }))}
                    placeholder="Empresa Ejemplo"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Giro Empresarial</label>
                  <input
                    type="text"
                    value={nuevaEmpresa.giro}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, giro: e.target.value }))}
                    placeholder="Venta de equipos industriales"
                  />
                </div>
                <div className="form-group">
                  <label>Email Contacto</label>
                  <input
                    type="email"
                    value={nuevaEmpresa.email}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contacto@empresa.cl"
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    value={nuevaEmpresa.telefono}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, telefono: e.target.value }))}
                    placeholder="+56 2 2345 6789"
                  />
                </div>
                <div className="form-group full-width" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Administrador Principal de la Empresa</h3>
                </div>
                <div className="form-group">
                  <label>Nombre del Admin *</label>
                  <input
                    type="text"
                    value={nuevaEmpresa.adminNombre}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, adminNombre: e.target.value }))}
                    placeholder="Juan Pérez"
                  />
                </div>
                <div className="form-group">
                  <label>Email del Admin *</label>
                  <input
                    type="email"
                    value={nuevaEmpresa.adminEmail}
                    onChange={(e) => {
                      setNuevaEmpresa(prev => ({ ...prev, adminEmail: e.target.value }));
                      verificarEmailAdmin(e.target.value);
                    }}
                    onBlur={(e) => verificarEmailAdmin(e.target.value)}
                    placeholder="admin@empresa.cl"
                    style={{
                      borderColor: adminEmailExists === true ? '#ff9800' : adminEmailExists === false ? '#4caf50' : undefined
                    }}
                  />
                  {checkingEmail && (
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Verificando email...
                    </small>
                  )}
                  {adminEmailExists === true && existingUserInfo && (
                    <div style={{
                      marginTop: '8px',
                      padding: '12px',
                      background: '#e3f2fd',
                      border: '2px solid #2196f3',
                      borderRadius: '6px',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}>
                      <strong style={{ color: '#1976d2' }}>ℹ️ Usuario Existente</strong>
                      <br />
                      <strong>{existingUserInfo.nombre}</strong>
                      <br />
                      Ya administra {existingUserInfo.empresas} empresa{existingUserInfo.empresas !== 1 ? 's' : ''}
                      <br />
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        Se usará su cuenta existente. Puedes asignar una contraseña diferente para esta empresa.
                      </span>
                    </div>
                  )}
                  {adminEmailExists === false && (
                    <small style={{ color: '#4caf50', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      ✓ Email disponible - Se creará nuevo usuario
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    Contraseña para esta Empresa *
                    {adminEmailExists === true && (
                      <span style={{ fontSize: '12px', color: '#2196f3', fontWeight: 'normal', marginLeft: '8px' }}>
                        (Puede ser diferente a otras empresas)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={nuevaEmpresa.adminPassword || 'Admin123!'}
                    onChange={(e) => setNuevaEmpresa(prev => ({ ...prev, adminPassword: e.target.value }))}
                    placeholder="Contraseña para esta empresa"
                  />
                  {adminEmailExists === true && (
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Esta será la contraseña específica para <strong>{nuevaEmpresa.nombreFantasia}</strong>
                    </small>
                  )}
                </div>
              </div>
              {/* Espaciador invisible para permitir scroll completo en móvil */}
              <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
            </div>
                <div className="modal-footer" style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: '100vw',
                  background: 'white',
                  borderTop: '3px solid #000',
                  padding: '20px',
                  zIndex: 999999,
                  display: 'flex',
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  gap: '12px',
                  boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                  <button
                    className="btn-secondary"
                    onClick={() => cerrarModalCrearEmpresa()}
                    style={{ width: window.innerWidth <= 768 ? '100%' : 'auto', padding: '14px', fontSize: '16px' }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleCrearEmpresa}
                    style={{ width: window.innerWidth <= 768 ? '100%' : 'auto', padding: '14px', fontSize: '16px' }}
                  >
                    Crear Empresa
                  </button>
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 40px',
                gap: '24px',
                minHeight: '400px'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: actionSuccess.type === 'success' ? themeColor : '#ffebee',
                  border: `3px solid ${actionSuccess.type === 'success' ? '#000' : '#d32f2f'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'scaleIn 0.3s ease-out'
                }}>
                  {actionSuccess.type === 'success' ? (
                    <CheckCircle size={60} style={{ color: '#000' }} />
                  ) : (
                    <AlertCircle size={60} style={{ color: '#d32f2f' }} />
                  )}
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#000',
                  textAlign: 'center'
                }}>
                  {actionSuccess.type === 'success' ? '¡Empresa Creada!' : 'Error al Crear Empresa'}
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: '#333',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                  maxWidth: '500px'
                }}>
                  {actionSuccess.message}
                </p>
                <button
                  onClick={() => cerrarModalCrearEmpresa()}
                  style={{
                    marginTop: '20px',
                    padding: '12px 32px',
                    background: themeColor,
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#000',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para crear nuevo Super Admin */}
      {modalCrearSuperAdmin && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header" style={{
              background: themeColor,
              borderBottom: '3px solid #000'
            }}>
              <h2 style={{ color: '#000', margin: 0, fontSize: '24px', fontWeight: '700' }}>Crear Nuevo Super Administrador</h2>
              <button onClick={() => setModalCrearSuperAdmin(false)} style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#000',
                fontWeight: 'bold'
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombre Completo *</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    placeholder="Juan Pérez"
                    value=""
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="super-admin-input"
                    placeholder="admin@paperboat.cl"
                    value=""
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    className="super-admin-input"
                    placeholder="+56 9 1234 5678"
                    value=""
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Contraseña Temporal *</label>
                  <input
                    type="password"
                    className="super-admin-input"
                    placeholder="Mínimo 8 caracteres"
                    value=""
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Notas</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    placeholder="Responsabilidades o notas adicionales"
                    value=""
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setModalCrearSuperAdmin(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  alert('✅ Super Admin creado exitosamente');
                  setModalCrearSuperAdmin(false);
                }}
              >
                Crear Super Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar Super Admin */}
      {modalEditarSuperAdmin && superAdminEditando && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header" style={{
              background: themeColor,
              borderBottom: '3px solid #000'
            }}>
              <h2 style={{ color: '#000', margin: 0, fontSize: '24px', fontWeight: '700' }}>Editar Super Administrador</h2>
              <button onClick={cerrarModalEditarSuperAdmin} style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#000',
                fontWeight: 'bold'
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    value={editFormData.firstName || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Apellido *</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    value={editFormData.lastName || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="super-admin-input"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    className="super-admin-input"
                    value={editFormData.phone || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+56 9 1234 5678"
                    autoComplete="off"
                  />
                </div>
                <div className="form-group full-width" style={{
                  background: '#e3f2fd',
                  border: '2px solid #2196f3',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '12px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#1565c0' }}>
                    <strong>🔒 Seguridad de Contraseñas:</strong><br />
                    Por razones de seguridad, solo el propio usuario puede cambiar su contraseña desde su perfil.
                    Esto evita que administradores accedan a cuentas de otros usuarios.
                  </p>
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    className="super-admin-input"
                    value={editFormData.isActive ? 'activo' : 'inactivo'}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, isActive: e.target.value === 'activo' }))}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={cerrarModalEditarSuperAdmin}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => actualizarSuperAdmin(editFormData)}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar empresa */}
      {modalEditarEmpresa && empresaEditando && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header" style={{
              background: themeColor,
              borderBottom: '3px solid #000'
            }}>
              <h2 style={{ color: '#000', margin: 0, fontSize: '24px', fontWeight: '700' }}>Editar Empresa</h2>
              <button onClick={cerrarModalEditarEmpresa} style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#000',
                fontWeight: 'bold'
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>RUT</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    value={empresaFormData.taxId || ''}
                    onChange={(e) => handleRutChange(e.target.value, 'empresaRut')}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Razón Social</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    value={empresaFormData.legalName || ''}
                    onChange={(e) => setEmpresaFormData(prev => ({ ...prev, legalName: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre Fantasía *</label>
                  <input
                    type="text"
                    className="super-admin-input"
                    value={empresaFormData.tradeName || ''}
                    onChange={(e) => setEmpresaFormData(prev => ({ ...prev, tradeName: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="super-admin-input"
                    value={empresaFormData.email || ''}
                    onChange={(e) => setEmpresaFormData(prev => ({ ...prev, email: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    className="super-admin-input"
                    value={empresaFormData.phone || ''}
                    onChange={(e) => setEmpresaFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+56 2 2345 6789"
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    className="super-admin-input"
                    value={empresaFormData.isActive ? 'activa' : 'inactiva'}
                    onChange={(e) => setEmpresaFormData(prev => ({ ...prev, isActive: e.target.value === 'activa' }))}
                  >
                    <option value="activa">Activa</option>
                    <option value="inactiva">Inactiva</option>
                  </select>
                </div>
              </div>
              {/* Espaciador invisible para permitir scroll completo en móvil */}
              <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
            </div>
            <div className="modal-footer" style={{
              position: window.innerWidth <= 768 ? 'fixed' : 'relative',
              bottom: window.innerWidth <= 768 ? 0 : 'auto',
              left: window.innerWidth <= 768 ? 0 : 'auto',
              right: window.innerWidth <= 768 ? 0 : 'auto',
              width: window.innerWidth <= 768 ? '100vw' : 'auto',
              background: 'white',
              borderTop: '3px solid #000',
              padding: '20px',
              zIndex: 999999,
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              gap: '12px',
              boxShadow: window.innerWidth <= 768 ? '0 -4px 12px rgba(0, 0, 0, 0.15)' : 'none'
            }}>
              <button
                className="btn-secondary"
                onClick={cerrarModalEditarEmpresa}
                style={{ width: window.innerWidth <= 768 ? '100%' : 'auto', padding: '14px', fontSize: '16px' }}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => actualizarEmpresa(empresaFormData)}
                style={{ width: window.innerWidth <= 768 ? '100%' : 'auto', padding: '14px', fontSize: '16px' }}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver administradores de empresa */}
      {modalAdministradores && empresaAdministradores && (
        <div className="modal-overlay">
          <div className="modal-container modal-administradores" style={{
            background: 'white',
            backgroundColor: 'white'
          }}>
            <div className="modal-header" style={{
              background: themeColor,
              borderBottom: '3px solid #000'
            }}>
              <h2 style={{ color: '#000', margin: 0, fontSize: '24px', fontWeight: '700' }}>
                Administradores de {empresaAdministradores.empresa.tradeName}
              </h2>
              <button onClick={cerrarModalAdministradores} style={{
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#000',
                fontWeight: 'bold'
              }}>×</button>
            </div>
            <div className="modal-body">
              {showCreateNew ? (
                <div className="create-admin-form" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {creatingAdmin ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '60px 20px',
                      gap: '20px',
                      textAlign: 'center'
                    }}>
                      <Clock size={48} style={{ color: themeColor, animation: 'spin 2s linear infinite' }} />
                      <h3 style={{ margin: 0, color: '#000', fontSize: '20px', fontWeight: '700' }}>
                        Agregando a {newUserForm.firstName} a los administradores de {empresaAdministradores.empresa.tradeName}
                      </h3>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        Por favor espera mientras creamos el nuevo administrador...
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                          Crear Nuevo Administrador
                        </h3>
                    <button
                      onClick={() => setShowCreateNew(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      ←
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                          Nombre
                        </label>
                        <input
                          type="text"
                          value={newUserForm.firstName}
                          onChange={(e) => setNewUserForm({...newUserForm, firstName: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #000',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                          }}
                          placeholder="Nombre"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                          Apellido (Opcional)
                        </label>
                        <input
                          type="text"
                          value={newUserForm.lastName}
                          onChange={(e) => setNewUserForm({...newUserForm, lastName: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #000',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                          }}
                          placeholder="Apellido"
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={newUserForm.email}
                        onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #000',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: 'white',
                          transition: 'all 0.2s'
                        }}
                        onFocus={(e) => {
                          e.target.style.background = localStorage.getItem('theme-primary-color') || '#c4fffc';
                          e.target.style.borderColor = '#000';
                        }}
                        onBlur={(e) => {
                          e.target.style.background = 'white';
                          e.target.style.borderColor = '#000';
                        }}
                        placeholder="email@empresa.com"
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                          Teléfono (Opcional)
                        </label>
                        <input
                          type="tel"
                          value={newUserForm.phone}
                          onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #000',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                          }}
                          placeholder="+56999999999"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                          Cargo (Opcional)
                        </label>
                        <select
                          value={newUserForm.position}
                          onChange={(e) => setNewUserForm({...newUserForm, position: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #000',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'all 0.2s',
                            background: 'white'
                          }}
                        >
                          <option value="">Seleccione un cargo</option>
                          {Array.isArray(positions) && positions.map(pos => (
                            <option key={pos.id} value={pos.name}>
                              {pos.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                          Departamento (Opcional)
                        </label>
                        <input
                          type="text"
                          value={newUserForm.department}
                          onChange={(e) => setNewUserForm({...newUserForm, department: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #000',
                            borderRadius: '6px',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                          }}
                          placeholder="Ej: Ventas, Finanzas, Operaciones"
                        />
                      </div>
                    </div>

                    {/* Sección de Contraseña */}
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#000' }}>
                        Contraseña
                      </h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
                        La contraseña debe contener: 8-20 caracteres, mayúsculas, minúsculas, números y símbolos (!@#$%^&*)
                      </p>

                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                            Contraseña
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={newUserForm.password}
                              onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '10px 40px 10px 12px',
                                border: '2px solid #000',
                                borderRadius: '6px',
                                fontSize: '14px',
                                transition: 'all 0.2s'
                              }}
                              placeholder="Ejemplo: MiClave@2024"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#666'
                              }}
                            >
                              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                            Confirmar Contraseña
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={newUserForm.confirmPassword}
                              onChange={(e) => setNewUserForm({...newUserForm, confirmPassword: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '10px 40px 10px 12px',
                                border: '2px solid #000',
                                borderRadius: '6px',
                                fontSize: '14px',
                                transition: 'all 0.2s'
                              }}
                              placeholder="Repetir contraseña"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              style={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#666'
                              }}
                            >
                              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Validación de contraseña en tiempo real */}
                      {newUserForm.password && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: '#f9f9f9',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#333' }}>
                            Requisitos de contraseña:
                          </p>
                          {(() => {
                            const validation = validatePassword(newUserForm.password);
                            return (
                              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                <li style={{
                                  fontSize: '13px',
                                  padding: '2px 0',
                                  color: validation.hasMinLength && validation.hasMaxLength ? '#22c55e' : '#dc2626',
                                  fontWeight: validation.hasMinLength && validation.hasMaxLength ? '600' : 'normal'
                                }}>
                                  ✓ 8-20 caracteres
                                </li>
                                <li style={{
                                  fontSize: '13px',
                                  padding: '2px 0',
                                  color: validation.hasUpperCase ? '#22c55e' : '#dc2626',
                                  fontWeight: validation.hasUpperCase ? '600' : 'normal'
                                }}>
                                  ✓ Mayúsculas
                                </li>
                                <li style={{
                                  fontSize: '13px',
                                  padding: '2px 0',
                                  color: validation.hasLowerCase ? '#22c55e' : '#dc2626',
                                  fontWeight: validation.hasLowerCase ? '600' : 'normal'
                                }}>
                                  ✓ Minúsculas
                                </li>
                                <li style={{
                                  fontSize: '13px',
                                  padding: '2px 0',
                                  color: validation.hasNumber ? '#22c55e' : '#dc2626',
                                  fontWeight: validation.hasNumber ? '600' : 'normal'
                                }}>
                                  ✓ Números
                                </li>
                                <li style={{
                                  fontSize: '13px',
                                  padding: '2px 0',
                                  color: validation.hasSymbol ? '#22c55e' : '#dc2626',
                                  fontWeight: validation.hasSymbol ? '600' : 'normal'
                                }}>
                                  ✓ Símbolos (!@#$%^&*)
                                </li>
                              </ul>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                      <button
                        onClick={() => setShowCreateNew(false)}
                        className="cancel-button"
                        style={{
                          flex: 1,
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={createNewAdmin}
                        disabled={!isFormValid}
                        style={{
                          flex: 1,
                          padding: '12px 24px',
                          background: isFormValid ? themeColor : '#e0e0e0',
                          border: '2px solid #000',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isFormValid ? 'pointer' : 'not-allowed',
                          opacity: isFormValid ? 1 : 0.6,
                          color: '#000'
                        }}
                      >
                        Crear Administrador
                      </button>
                    </div>
                  </div>
                      </>
                    )}
                </div>
              ) : showAddAdmin ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                      Agregar Administrador
                    </h3>
                    <button
                      onClick={() => setShowAddAdmin(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      ←
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <button
                      onClick={() => {
                        setShowCreateNew(true);
                        loadPositions(empresaAdministradores.empresa.id);
                      }}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        background: themeColor,
                        border: '2px solid #000',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Plus size={16} />
                      Crear Nuevo Usuario
                    </button>
                  </div>

                  {promotingUser ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '40px',
                      gap: '16px'
                    }}>
                      <Clock size={48} style={{ color: themeColor }} />
                      <h3 style={{ margin: 0, color: '#000', fontSize: '18px' }}>
                        Promoviendo usuario...
                      </h3>
                      <p style={{ margin: 0, color: '#666', textAlign: 'center' }}>
                        Por favor espera mientras se actualiza el rol del usuario
                      </p>
                    </div>
                  ) : teamMembers.length > 0 ? (
                    <>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        O selecciona un miembro del equipo para promover a administrador:
                      </p>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {teamMembers.map((member) => (
                          <div key={member.id} className="team-member-card" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '16px',
                            background: themeColor,
                            border: '2px solid #000',
                            borderRadius: '8px',
                            transition: 'all 0.3s'
                          }}>
                            <div className="team-member-avatar" style={{
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%',
                              background: '#fff',
                              border: '2px solid #000',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: '700',
                              color: '#000',
                              flexShrink: 0
                            }}>
                              {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                            </div>

                            <div className="team-member-info" style={{ flex: 1, minWidth: 0 }}>
                              <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#000' }}>
                                {member.firstName} {member.lastName}
                              </h4>
                              <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#000', opacity: 0.8, wordBreak: 'break-word' }}>
                                {member.email}
                              </p>
                              <span style={{
                                fontSize: '12px',
                                background: '#000',
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: '600',
                                display: 'inline-block'
                              }}>
                                {getRoleDisplayName(member.role)}
                              </span>
                            </div>

                            <button
                              className="team-member-promote-btn"
                              onClick={() => promoteToAdmin(member.id)}
                              disabled={promotingUser === member.id}
                              style={{
                                padding: '8px 16px',
                                background: promotingUser === member.id ? '#f0f0f0' : '#fff',
                                border: '2px solid #000',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: promotingUser === member.id ? 'not-allowed' : 'pointer',
                                color: promotingUser === member.id ? '#666' : '#000',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: promotingUser === member.id ? 0.7 : 1,
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}
                            >
                              {promotingUser === member.id ? (
                                <Clock size={16} />
                              ) : (
                                <Crown size={16} />
                              )}
                              {promotingUser === member.id ? 'Promoviendo...' : 'Promover'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#666'
                    }}>
                      <p>No hay miembros del equipo disponibles para promover.</p>
                      <p style={{ fontSize: '14px', marginTop: '8px' }}>
                        Todos los miembros actuales ya son administradores.
                      </p>
                    </div>
                  )}
                </div>
              ) : actionSuccess ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '60px 20px',
                  minHeight: '400px',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: actionSuccess.type === 'remove' ? '#ffebee' : themeColor,
                    border: `3px solid ${actionSuccess.type === 'remove' ? '#d32f2f' : '#000'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    animation: 'fadeIn 0.5s ease'
                  }}>
                    {actionSuccess.type === 'remove' ? (
                      <Trash2 size={40} style={{ color: '#d32f2f' }} />
                    ) : (
                      <CheckCircle size={40} style={{ color: '#000' }} />
                    )}
                  </div>

                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    margin: '0 0 16px 0',
                    color: '#000',
                    textAlign: 'center'
                  }}>
                    ¡Acción completada!
                  </h3>

                  <p style={{
                    fontSize: '18px',
                    color: '#333',
                    margin: '0',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}>
                    {actionSuccess.message}
                  </p>

                  <div style={{
                    marginTop: '32px',
                    padding: '12px 24px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    border: '2px solid #000',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Clock size={16} />
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      Volviendo a la lista...
                    </span>
                  </div>
                </div>
              ) : showDeleteConfirm && adminToDelete ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '40px 20px',
                  minHeight: '400px',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: themeColor,
                    border: '3px solid #000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    fontSize: '28px',
                    fontWeight: '700'
                  }}>
                    {adminToDelete.firstName.charAt(0)}{adminToDelete.lastName.charAt(0)}
                  </div>

                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    margin: '0 0 8px 0',
                    color: '#000'
                  }}>
                    {adminToDelete.firstName} {adminToDelete.lastName}
                  </h3>

                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    margin: '0 0 32px 0'
                  }}>
                    {adminToDelete.email}
                  </p>

                  <p style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: '0 0 24px 0',
                    color: '#000',
                    textAlign: 'center'
                  }}>
                    ¿Qué deseas hacer con este administrador?
                  </p>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '100%',
                    maxWidth: '400px'
                  }}>
                    <button
                      onClick={() => changeAdminRole('Editor')}
                      style={{
                        padding: '16px 24px',
                        background: 'white',
                        border: '2px solid #000',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = themeColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <span>Cambiar a Editor</span>
                      <Edit size={18} />
                    </button>

                    <button
                      onClick={() => changeAdminRole('Miembro')}
                      style={{
                        padding: '16px 24px',
                        background: 'white',
                        border: '2px solid #000',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = themeColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <span>Cambiar a Miembro</span>
                      <Users size={18} />
                    </button>

                    <button
                      onClick={removeAdminFromCompany}
                      style={{
                        padding: '16px 24px',
                        background: '#ffebee',
                        border: '2px solid #d32f2f',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s',
                        marginTop: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ffcdd2';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffebee';
                      }}
                    >
                      <span>Eliminar de la empresa</span>
                      <Trash2 size={18} />
                    </button>

                    <button
                      onClick={cancelDeleteAdmin}
                      style={{
                        padding: '12px 24px',
                        background: '#f5f5f5',
                        border: '2px solid #999',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#666',
                        cursor: 'pointer',
                        marginTop: '8px',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f5f5f5';
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#000' }}>
                    Lista de Administradores
                  </h3>
                  <button
                    onClick={openAddAdmin}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      background: themeColor,
                      border: '2px solid #000',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      color: '#000',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Plus size={16} />
                    Agregar Admin
                  </button>
                </div>

                <div className="admin-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'white' }}>
                  {empresaAdministradores.administradores.map((admin) => (
                  <div key={admin.id} className="admin-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px',
                    background: '#f8f9fa',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}>
                    <div className="admin-avatar-large" style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: themeColor,
                      border: '3px solid #000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#000'
                    }}>
                      {admin.firstName.charAt(0)}{admin.lastName.charAt(0)}
                    </div>
                    <div className="admin-details-full" style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#000'
                      }}>{admin.firstName} {admin.lastName}</h3>
                      <p className="admin-role" style={{
                        margin: '0 0 12px 0',
                        fontSize: '14px',
                        color: '#000',
                        fontWeight: '600'
                      }}>{getRoleDisplayName(admin.role)}</p>
                      <div className="admin-contact" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        <div className="contact-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <Mail size={16} style={{ color: '#000', flexShrink: 0 }} />
                          <span style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            flex: 1
                          }}>{admin.email}</span>
                        </div>
                        <div className="contact-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <Phone size={16} style={{ color: '#000' }} />
                          <span>{admin.phone}</span>
                        </div>
                        <div className="contact-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <Calendar size={16} style={{ color: '#000' }} />
                          <span>Último acceso: {admin.lastLogin === 'Nunca' ? 'Nunca' : new Date(admin.lastLogin).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="admin-actions" style={{
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <button
                        className="btn-icon-action edit"
                        onClick={() => handleEditAdmin(admin)}
                        title="Editar administrador"
                        style={{
                          padding: '8px',
                          background: '#fff',
                          border: '2px solid #000',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = themeColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        <Edit size={16} style={{ color: '#000' }} />
                      </button>
                      <button
                        className="btn-icon-action delete"
                        onClick={() => handleDeleteAdmin(admin)}
                        title="Gestionar permisos"
                        style={{
                          padding: '8px',
                          background: '#fff',
                          border: '2px solid #000',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = themeColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        <Settings size={16} style={{ color: '#000' }} />
                      </button>
                    </div>
                  </div>
                ))}
                </div>
              </>
              )}
              {/* Espaciador invisible para permitir scroll completo en móvil */}
              <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
            </div>
            <div className="modal-footer" style={{
              position: window.innerWidth <= 768 ? 'fixed' : 'relative',
              bottom: window.innerWidth <= 768 ? 0 : 'auto',
              left: window.innerWidth <= 768 ? 0 : 'auto',
              right: window.innerWidth <= 768 ? 0 : 'auto',
              width: window.innerWidth <= 768 ? '100vw' : 'auto',
              padding: '20px 24px',
              borderTop: '2px solid #000',
              background: 'white',
              zIndex: 999999,
              display: 'flex',
              justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-end',
              boxShadow: window.innerWidth <= 768 ? '0 -4px 12px rgba(0, 0, 0, 0.15)' : 'none'
            }}>
              <button
                className="btn-secondary"
                onClick={cerrarModalAdministradores}
                style={{
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  maxWidth: window.innerWidth <= 768 ? '100%' : 'auto',
                  padding: window.innerWidth <= 768 ? '14px' : '10px 24px',
                  background: themeColor,
                  border: '2px solid #000',
                  borderRadius: '8px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: '#000'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar administrador de empresa */}
      {modalEditarAdmin && editFormData.id && (
        <div className="modal-overlay">
          <div className="modal-container" style={{
            background: 'white !important',
            backgroundColor: 'white !important',
            border: '3px solid #000',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '90%'
          }}>
            <div className="modal-header" style={{
              background: themeColor,
              borderBottom: '3px solid #000',
              padding: '20px 24px'
            }}>
              <h2>Editar Administrador</h2>
              <button onClick={() => setModalEditarAdmin(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={editFormData.firstName || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        background: 'white !important',
                        color: '#000 !important'
                      }}
                      placeholder="Nombre"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                      Apellido (Opcional)
                    </label>
                    <input
                      type="text"
                      value={editFormData.lastName || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        background: 'white !important',
                        color: '#000 !important'
                      }}
                      placeholder="Apellido"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #000',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#000',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.background = themeColor;
                      e.target.style.borderColor = '#000';
                      e.target.style.color = '#000';
                    }}
                    onBlur={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#000';
                      e.target.style.color = '#000';
                    }}
                    placeholder="email@empresa.com"
                  />
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                      Teléfono (Opcional)
                    </label>
                    <input
                      type="tel"
                      value={editFormData.phone || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        background: 'white !important',
                        color: '#000 !important'
                      }}
                      placeholder="+56999999999"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                      Cargo (Opcional)
                    </label>
                    <select
                      value={editFormData.position || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, position: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        background: 'white !important',
                        color: '#000 !important'
                      }}
                    >
                      <option value="">Seleccione un cargo</option>
                      {Array.isArray(positions) && positions.map(pos => (
                        <option key={pos.id} value={pos.name}>
                          {pos.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#000', marginBottom: '8px', display: 'block' }}>
                      Rol
                    </label>
                    <select
                      value={editFormData.role || 'admin'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, role: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #000',
                        borderRadius: '6px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        background: 'white !important',
                        color: '#000 !important'
                      }}
                    >
                      <option value="admin">Administrador</option>
                      <option value="editor">Editor</option>
                      <option value="member">Miembro</option>
                    </select>
                  </div>
                </div>

                {/* Mensaje de error */}
                {editFormData.error && (
                  <div style={{
                    background: '#ffebee',
                    border: '2px solid #d32f2f',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ color: '#d32f2f', fontSize: '16px' }}>⚠️</span>
                    <span style={{ color: '#d32f2f', fontSize: '14px', fontWeight: '500' }}>
                      {editFormData.error}
                    </span>
                  </div>
                )}

                {/* Campo de contraseña */}
                <div style={{
                  background: themeColor,
                  border: '2px solid #000',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '12px'
                }}>
                  <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '8px', display: 'block' }}>
                    🔐 Cambiar Contraseña para '{editFormData.email}' en {empresaAdministradores?.empresa?.tradeName}
                  </label>
                  <input
                    type="text"
                    value={editFormData.newPassword || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #000',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white',
                      color: '#000'
                    }}
                    placeholder="Dejar vacío para no cambiar"
                  />
                  <small style={{ color: '#000', fontSize: '12px', marginTop: '8px', display: 'block', fontWeight: '500' }}>
                    Esta es la contraseña específica para esta empresa. No afectará otras empresas.
                  </small>
                </div>
              </div>
              {/* Espaciador invisible para permitir scroll completo en móvil */}
              <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
            </div>
            <div className="modal-footer" style={{
              position: window.innerWidth <= 768 ? 'fixed' : 'relative',
              bottom: window.innerWidth <= 768 ? 0 : 'auto',
              left: window.innerWidth <= 768 ? 0 : 'auto',
              right: window.innerWidth <= 768 ? 0 : 'auto',
              width: window.innerWidth <= 768 ? '100vw' : 'auto',
              padding: '20px 24px',
              borderTop: '2px solid #000',
              background: 'white',
              zIndex: 999999,
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-end',
              gap: '12px',
              boxShadow: window.innerWidth <= 768 ? '0 -4px 12px rgba(0, 0, 0, 0.15)' : 'none'
            }}>
              <button
                onClick={() => {
                  setModalEditarAdmin(false);
                  setEditFormData({ ...editFormData, error: null, newPassword: '' });
                }}
                style={{
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  padding: window.innerWidth <= 768 ? '14px' : '10px 24px',
                  background: 'white',
                  border: '2px solid #000',
                  borderRadius: '8px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    // Actualizar usuario
                    const updateData = {
                      firstName: editFormData.firstName,
                      lastName: editFormData.lastName,
                      email: editFormData.email,
                      phone: editFormData.phone,
                      role: editFormData.role,
                      position: editFormData.position,
                      companyId: empresaAdministradores.empresa.id
                    };

                    // Si hay nueva contraseña, agregarla
                    if (editFormData.newPassword) {
                      updateData.password = editFormData.newPassword;
                    }

                    await api.patch(`/superadmin/users/${editFormData.id}`, updateData);

                    // Recargar administradores
                    const response = await api.get(`/superadmin/companies/${empresaAdministradores.empresa.id}/users`);
                    const administradores = response.data
                      .filter(userCompany => userCompany.role === 'admin' && userCompany.isActive)
                      .map(userCompany => ({
                        id: userCompany.User.id,
                        firstName: userCompany.User.firstName,
                        lastName: userCompany.User.lastName,
                        email: userCompany.User.email,
                        role: userCompany.role,
                        phone: userCompany.User.phone || '+56999999999',
                        lastLogin: userCompany.User.lastLogin || 'Nunca'
                      }));

                    setEmpresaAdministradores({ ...empresaAdministradores, administradores });
                    setModalEditarAdmin(false);
                    setEditFormData({ ...editFormData, newPassword: '' }); // Limpiar contraseña
                  } catch (error) {
                    console.error('Error actualizando administrador:', error);
                    // Mostrar error en el formulario
                    setEditFormData(prev => ({
                      ...prev,
                      error: error.response?.data?.message || 'Error al actualizar administrador'
                    }));
                  }
                }}
                style={{
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  padding: window.innerWidth <= 768 ? '14px' : '10px 24px',
                  background: themeColor,
                  border: '2px solid #000',
                  borderRadius: '8px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Eliminar Empresa */}
      {showDeleteCompanyConfirm && empresaToDelete && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header" style={{ background: deleteCompanySuccess?.type === 'success' ? themeColor : '#ff4444' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: deleteCompanySuccess?.type === 'success' ? '#000' : 'white' }}>
                {deleteCompanySuccess ? (deleteCompanySuccess.type === 'success' ? '¡Empresa Eliminada!' : 'Error al Eliminar') : '¿Eliminar Empresa?'}
              </h2>
              <button
                onClick={() => {
                  setShowDeleteCompanyConfirm(false);
                  setEmpresaToDelete(null);
                  setDeleteCompanySuccess(false);
                }}
                className="close-button"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: deleteCompanySuccess?.type === 'success' ? '#000' : 'white'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {deleteCompanySuccess ? (
                // Vista de Éxito o Error
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '60px 40px',
                  textAlign: 'center',
                  gap: '24px'
                }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: deleteCompanySuccess.type === 'success' ? themeColor : '#ffebee',
                    border: `3px solid ${deleteCompanySuccess.type === 'success' ? '#000' : '#d32f2f'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'scaleIn 0.3s ease-out'
                  }}>
                    {deleteCompanySuccess.type === 'success' ? (
                      <CheckCircle size={60} color="#000" />
                    ) : (
                      <AlertCircle size={60} color="#d32f2f" />
                    )}
                  </div>

                  {deleteCompanySuccess.type === 'success' ? (
                    <>
                      <p style={{
                        fontSize: '16px',
                        color: '#666',
                        margin: 0
                      }}>
                        <strong>{deleteCompanySuccess.empresaNombre}</strong>
                      </p>

                      <p style={{
                        fontSize: '16px',
                        color: '#333',
                        margin: 0,
                        lineHeight: '1.8'
                      }}>
                        Se ha eliminado exitosamente la empresa y todos sus datos asociados.
                      </p>
                    </>
                  ) : (
                    <p style={{
                      fontSize: '16px',
                      color: '#333',
                      margin: 0,
                      lineHeight: '1.8',
                      whiteSpace: 'pre-line'
                    }}>
                      {deleteCompanySuccess.message}
                    </p>
                  )}

                  {/* Espaciador invisible para permitir scroll completo en móvil */}
                  <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
                </div>
              ) : (
                // Vista de Confirmación
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '40px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#ff4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px'
                  }}>
                    <Trash2 size={40} color="white" />
                  </div>

                  <p style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#000',
                    margin: '0 0 12px 0'
                  }}>
                    {empresaToDelete.tradeName}
                  </p>

                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    margin: '0 0 32px 0',
                    lineHeight: '1.6'
                  }}>
                    Esta acción <strong>eliminará permanentemente</strong> la empresa y <strong>todos sus datos asociados</strong>:
                    <br/>• Todos los usuarios ({empresaToDelete.userCount || 0})
                    <br/>• Todos los administradores
                    <br/>• Todas las órdenes y registros
                    <br/>• Configuraciones y ajustes
                    <br/><br/>
                    <strong style={{ color: '#ff4444' }}>Esta acción no se puede deshacer.</strong>
                  </p>

                  {/* Espaciador invisible para permitir scroll completo en móvil */}
                  <div style={{ height: window.innerWidth <= 768 ? '400px' : '0px', width: '100%' }}></div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{
              position: window.innerWidth <= 768 ? 'fixed' : 'relative',
              bottom: window.innerWidth <= 768 ? 0 : 'auto',
              left: window.innerWidth <= 768 ? 0 : 'auto',
              right: window.innerWidth <= 768 ? 0 : 'auto',
              width: window.innerWidth <= 768 ? '100vw' : 'auto',
              background: 'white',
              borderTop: '3px solid #000',
              padding: window.innerWidth <= 768 ? '20px 20px max(20px, env(safe-area-inset-bottom)) 20px' : '20px 24px',
              zIndex: window.innerWidth <= 768 ? 999999 : 'auto',
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              gap: '12px',
              boxShadow: window.innerWidth <= 768 ? '0 -4px 12px rgba(0, 0, 0, 0.15)' : 'none',
              justifyContent: deleteCompanySuccess ? 'center' : 'flex-end'
            }}>
              {deleteCompanySuccess ? (
                <button
                  onClick={() => {
                    setShowDeleteCompanyConfirm(false);
                    setEmpresaToDelete(null);
                    setDeleteCompanySuccess(false);
                  }}
                  style={{
                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                    padding: '14px 32px',
                    background: themeColor,
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#000',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Cerrar
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowDeleteCompanyConfirm(false);
                      setEmpresaToDelete(null);
                    }}
                    style={{
                      flex: window.innerWidth <= 768 ? 'unset' : 1,
                      width: window.innerWidth <= 768 ? '100%' : 'auto',
                      padding: '14px 24px',
                      background: 'white',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>

                  <button
                    onClick={eliminarEmpresa}
                    style={{
                      flex: window.innerWidth <= 768 ? 'unset' : 1,
                      width: window.innerWidth <= 768 ? '100%' : 'auto',
                      padding: '14px 24px',
                      background: '#ff4444',
                      color: 'white',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Eliminar Empresa
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPanel;